"use client";

import { useLanguage } from "@/components/LanguageProvider";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { MobileNav } from "@/components/MobileNav";
import type { NavItem } from "@/components/MobileNav";
import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";

function hexToRgba(hex: string, opacity: number): string {
  const h = hex.replace("#", "").padEnd(6, "0");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity / 100})`;
}

const blurMap: Record<string, string> = {
  none: "backdrop-blur-none",
  sm: "backdrop-blur-sm",
  md: "backdrop-blur-md",
  lg: "backdrop-blur-lg",
  xl: "backdrop-blur-xl",
  "2xl": "backdrop-blur-2xl",
  "3xl": "backdrop-blur-3xl",
};

type Props = {
  navItems: NavItem[];
  brandName: string;
  brandFirst: string;
  brandLast: string;
  logoSrc: string;
  brandTextColor: string;
  headerBg: string;
  headerOpacity: number;
  headerBlur: string;
};

export function SiteHeaderClient({
  navItems,
  brandName,
  brandFirst,
  brandLast,
  logoSrc,
  brandTextColor,
  headerBg,
  headerOpacity,
  headerBlur,
}: Props) {
  const { t, dir, lang } = useLanguage();

  const bgStyle = useMemo(() => {
    if (headerBg) {
      return { backgroundColor: hexToRgba(headerBg, headerOpacity) };
    }
    return {
      backgroundColor: `color-mix(in srgb, var(--header-background) ${headerOpacity}%, transparent)`,
    };
  }, [headerBg, headerOpacity]);

  const blurClass = blurMap[headerBlur] ?? "backdrop-blur-xl";

  const translatedNav = useMemo(
    () =>
      navItems.map((item) => {
        const slug = item.href.replace(/^\//, "") || "home";
        const key = `nav.${slug}`;
        if (lang === "ar" && item.labelAr) {
          return { href: item.href, label: item.labelAr };
        }
        return { href: item.href, label: t(key) || item.label };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navItems, t, lang]
  );

  return (
    <>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-[var(--primary)] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg"
    >
      {t("a11y.skip")}
    </a>
    <header
      style={bgStyle}
      className={`sticky top-0 z-50 border-b border-[var(--border)]/60 shadow-sm shadow-slate-900/5 ${blurClass} backdrop-saturate-150 supports-[backdrop-filter]:bg-transparent`}
      dir={dir}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 md:px-6">
        <Link
          href="/"
          className={`flex items-center gap-2 font-extrabold tracking-tight${brandTextColor ? "" : " text-[var(--primary)]"}`}
          style={brandTextColor ? { color: brandTextColor } : undefined}
          aria-label={`${brandName} — ${t("nav.home")}`}
        >
          <Image
            src={logoSrc}
            alt=""
            width={36}
            height={36}
            className="h-9 w-auto"
            priority
          />
          <span>
            {brandFirst ? <>{brandFirst} </> : null}
            <span className={brandTextColor ? "" : "text-[var(--accent)]"}>
              {brandLast}
            </span>
          </span>
        </Link>

        <nav
          className="hidden items-center gap-8 md:flex"
          aria-label={t("nav.aria")}
        >
          {translatedNav.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="relative text-sm font-medium text-[var(--foreground)] transition hover:text-[var(--link)] after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-0 after:bg-[var(--accent)] after:transition-all hover:after:w-full"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            href="/search"
            className="rounded-md px-2 py-1.5 text-sm font-medium text-[var(--foreground)] transition hover:text-[var(--link)] max-md:min-h-11 max-md:min-w-11 max-md:inline-flex max-md:items-center max-md:justify-center md:inline-flex"
            aria-label={t("nav.search")}
          >
            <span className="md:hidden" aria-hidden>
              ⌕
            </span>
            <span className="hidden md:inline">{t("nav.search")}</span>
          </Link>
          <LanguageSwitcher />
          <MobileNav
            items={[
              ...translatedNav,
              { href: "/search", label: t("nav.search") },
            ]}
          />
        </div>
      </div>
    </header>
    </>
  );
}
